import { Alert, Button, Dialog } from '@mui/material'
import { useSnackbar } from 'notistack'
import React, { useContext, useState } from 'react'

import { useLoading } from 'providers/context/Loading'
import { triggerRemoveFromCartEvent } from 'services/Analytics'
import { removeBookableItemToCart } from 'services/Cart'
import { LOCATION_STATE_CONFIG } from 'utils/constants/Helpers'
import {
  ENHANCEMENT_REMOVED_MESSAGE,
  GENERAL_ERROR_MESSAGE,
} from 'utils/constants/Messages'
import { REMOVE_ENHANCEMENT_FAILED } from 'utils/sentry/sentry-messages'
import { captureError } from 'utils/sentry/sentry-utils'
import { OptionsTypes } from 'utils/types/enhancementTypes'
import { AppContext } from '../../../providers/context/App'

import './appointment.scss'

const AppointmentEnhancementList = () => {
  const { updateData, appMasterData } = useContext(AppContext)
  const { enqueueSnackbar } = useSnackbar()
  const [deleteModalOpen, setDeleteModalOpen] = useState('')
  const { setLoading } = useLoading()
  const purchasedMembership = appMasterData.cart.purchasedMembership
  const isDevEnv = process.env.REACT_APP_ENV === 'development'

  const { selectedEnhancements } = appMasterData

  const removeEnhancement = async (id: string) => {
    setLoading(true)
    const newEnhancements = (selectedEnhancements as OptionsTypes[]).filter(
      (enhancement) => enhancement.cartItemId !== id
    )

    const selectedEnhancement = (selectedEnhancements as OptionsTypes[]).find(
      (item) => item.cartItemId === id
    )

    try {
      const removeEnhancementFromCart = await removeBookableItemToCart(
        appMasterData.cart.cartId,
        id
      )

      if (removeEnhancementFromCart.removeCartSelectedItem?.cart.id) {
        if (newEnhancements.length === 0) {
          updateData({
            selectedEnhancements: newEnhancements,
            enhancement: false,
          })
        } else {
          updateData({ selectedEnhancements: newEnhancements })
        }
        enqueueSnackbar(ENHANCEMENT_REMOVED_MESSAGE, { variant: 'success' })
      }

      const ga4Data = {
        type: 'Booking',
        currency: 'USD',
        value: selectedEnhancement?.listPrice! / 100,
        items: selectedEnhancement?.name ?? '',
        item_name: appMasterData.selectedFacial ?? '',
        location_name: appMasterData.location.name ?? '',
        location_state:
          LOCATION_STATE_CONFIG[appMasterData?.location?.address?.state] ?? '',
        userId: appMasterData?.userInfo?.boulevard_id ?? '',
      }
      triggerRemoveFromCartEvent(ga4Data)
    } catch (error) {
      captureError(REMOVE_ENHANCEMENT_FAILED, {
        email: appMasterData.userInfo.email,
        location: appMasterData.location,
        newEnhancements,
        error,
        functionName: 'removeEnhancement',
      })
      enqueueSnackbar(GENERAL_ERROR_MESSAGE, { variant: 'error' })
    } finally {
      setDeleteModalOpen('')
      setLoading(false)
    }
  }

  const removeMembership = () => {
    updateData({
      cart: {
        ...appMasterData.cart,
        purchasedMembership: false,
      },
    })
  }

  return (
    <>
      {(!!selectedEnhancements.length || purchasedMembership) && (
        <div className="selected-enhancements">
          <div className="enhancements-list">
            <span className="selected-enhancement-label">
              {appMasterData.cart.purchasedMembership
                ? 'Selected items'
                : 'Selected enhancements'}
            </span>
            {(selectedEnhancements as OptionsTypes[])?.map((item) => (
              <Alert
                className="custom-alert"
                icon={false}
                onClose={() => {
                  setDeleteModalOpen(item.cartItemId as string)
                }}
                id={isDevEnv ? item.boulevard_sandbox_id : item.boulevard_id}
                key={item.id}
              >
                {item.name}
              </Alert>
            ))}
            {purchasedMembership && (
              <Alert
                className="custom-alert body-1"
                icon={false}
                onClose={removeMembership}
                id={'purchased-membership'}
              >
                Monthly Membership
              </Alert>
            )}
          </div>
          <Dialog
            open={!!deleteModalOpen}
            className="delete-enhancement-dialog"
          >
            <div className="delete-enhancement-dialog-text">
              <h3 className="h3">Remove Enhancement?</h3>
              <p>
                Are you sure you want to remove this from your appointment
                booking?
              </p>
            </div>
            <div className="delete-enhancement-dialog-buttons">
              <Button
                onClick={() => {
                  removeEnhancement(deleteModalOpen).catch((e) =>
                    console.error(e)
                  )
                }}
                className="delete-enhancement-confirm-button"
              >
                Remove
              </Button>
              <Button
                onClick={() => setDeleteModalOpen('')}
                className="delete-enhancement-cancel-button"
              >
                Keep
              </Button>
            </div>
          </Dialog>
        </div>
      )}
    </>
  )
}

export default React.memo(AppointmentEnhancementList)
